import cookies from 'js-cookie';

/**
 * The one place the admin session cookie is written, read and cleared (DUP-02 + DUP-04,
 * SC-1193). `apps/admin/src/utils/getAccessToken.ts` and
 * `apps/streamline/src/utils/getAccessToken.ts` are re-export shims over this module — nearly
 * 400 files import one of the three paths, so collapsing the *implementations* rather than the
 * import paths is what removes the drift without a repo-wide rename.
 *
 * That drift was real: this copy had no try/catch, so a malformed cookie threw an unhandled
 * SyntaxError here while the two app copies swallowed it. And because the write was duplicated
 * three ways, SEC-04 below was one bug in three places.
 */

const ACCESS_TOKEN_COOKIE = 'accessToken';

/**
 * Attributes shared by the write and the delete. The path has to match on both, or `remove`
 * quietly leaves the cookie in place and "sign out" does nothing.
 *
 * `secure` is derived, never hardcoded. Secure cookies are simply not stored over http, and
 * local dev serves admin on http://localhost:5102 — hardcoding `true` breaks local sign-in with
 * no error anywhere. This mirrors `isSecure` in streamline's tenantCookie.ts, which already had
 * it right.
 */
const cookieAttributes = (): { path: string; sameSite: 'strict'; secure: boolean } => ({
  path: '/',
  sameSite: 'strict',
  secure: typeof window !== 'undefined' && window.location.protocol === 'https:',
});

/**
 * Milliseconds since the epoch for a JWT's `exp`, or null when the token carries no usable one.
 *
 * Decoding happens HERE rather than at the call sites, which is what fixes SEC-04b: TenantSwitcher
 * read `.exp` straight off the raw token — a string — so `exp` was `undefined` and the expiry was
 * `NaN`. It went unnoticed because `useTenantSwitchMutation` mistyped the response as
 * `{ accessToken: JwtToken }`, and stayed inert only because the attribute was being written into
 * the JSON value instead of applied. Taking the raw token here means no caller can reintroduce it.
 *
 * The payload segment is decoded inline rather than with `jwt-decode`, deliberately. This module
 * is pulled into the SSR entry bundles of admin, streamline and web, and Vite externalises
 * dependencies in an SSR build — so an import here becomes a runtime `require` in the server
 * bundle. `jwt-decode` is a devDependency in admin and streamline and absent from web entirely,
 * so it is pruned from the production image: importing it 500s admin's SSR with
 * `ERR_MODULE_NOT_FOUND`, which is exactly what happened on the first sandbox deploy of this
 * change. Reading `exp` is a base64url parse of the middle segment; it does not justify making
 * three apps carry a new runtime dependency.
 *
 * No signature verification, and none is wanted: this only decides a cookie's expiry. The token
 * is verified by the API on every request, and a client that tampered with its own cookie expiry
 * would only be shortening or extending its own session, not gaining access.
 */
const expiryFromToken = (token: string): number | null => {
  try {
    const payloadSegment = token.split('.')[1];

    if (!payloadSegment) {
      return null;
    }

    // base64url -> base64, then pad to a multiple of 4; `atob` rejects a bare unpadded string.
    const base64 = payloadSegment.replace(/-/g, '+').replace(/_/g, '/');
    const padded = base64.padEnd(base64.length + ((4 - (base64.length % 4)) % 4), '=');
    const { exp } = JSON.parse(atob(padded));

    if (typeof exp !== 'number' || !Number.isFinite(exp)) {
      return null;
    }

    const expiresAt = exp * 1000;

    // A `Set-Cookie` whose Expires is in the past is a DELETE, not a short session. That was
    // harmless while `expires` was an inert JSON key; now that it is a real attribute, an
    // already-expired token — or a machine whose clock is wrong by more than the token lifetime —
    // would drop the cookie the instant it was written, and login would bounce silently back to
    // /login with nothing shown to the user. Fall back to a session cookie instead.
    return expiresAt > Date.now() ? expiresAt : null;
  } catch {
    // An undecodable token is the auth layer's problem, not this helper's. Write it as a session
    // cookie rather than blocking sign-in on a decode quirk.
    return null;
  }
};

/**
 * Writes the session cookie (SEC-04, SC-1193/SC-1200).
 *
 * The attributes go in `js-cookie`'s **third** argument. All three former call sites passed two
 * arguments and serialised `secure`, `path` and `expires` into the JSON value, where they were
 * inert: `Secure` was never set, so the admin JWT travelled over plaintext http, and `Expires`
 * was never set, so the cookie's lifetime stopped tracking the token's.
 *
 * `expires` must be a `Date`. js-cookie reads a bare number as a count of DAYS, so the old
 * `exp * 1000` would have meant roughly 1.8 billion days the moment it became a real attribute —
 * a bug that only surfaces once the flag placement is fixed.
 *
 * The value stays a JSON envelope with a `value` key on purpose. Every reader takes `.value` —
 * including the SSR unwrap in `apps/admin/server.mjs` — so old and new bundles read each other's
 * cookies and nobody is logged out by the deploy. Do not rename or re-nest that key.
 */
export const setAccessToken = (token: string): void => {
  const expiresAt = expiryFromToken(token);

  cookies.set(ACCESS_TOKEN_COOKIE, JSON.stringify({ name: ACCESS_TOKEN_COOKIE, value: token }), {
    ...cookieAttributes(),
    ...(expiresAt === null ? {} : { expires: new Date(expiresAt) }),
  });
};

/** The raw JWT, or null when there is no usable session cookie. Never throws. */
export const getAccessToken = (): string | null => {
  const accessToken = cookies.get(ACCESS_TOKEN_COOKIE);

  if (!accessToken) {
    return null;
  }

  try {
    const { value } = JSON.parse(accessToken) ?? {};

    // `JSON.parse` returns `any`, so the declared `string | null` is not enforced by the compiler:
    // a cookie of `{"value": {...}}` would be handed back typed as a string, become
    // `Authorization: Bearer [object Object]`, and crash `ProtectedRoute`'s `jwtDecode` on every
    // protected route instead of redirecting to /login. This is the one hardened reader and the
    // cookie is not HttpOnly yet, so the check is worth making at runtime.
    return typeof value === 'string' && value ? value : null;
  } catch {
    return null;
  }
};

/** Clears the session cookie. Attributes must match the write — see {@link cookieAttributes}. */
export const removeAccessToken = (): void => {
  cookies.remove(ACCESS_TOKEN_COOKIE, cookieAttributes());
};
